import React, { useState, useEffect, useCallback } from 'react';
import { 
  getEnrollmentsForApprovalAdmin, 
  approveEnrollmentCertificateAdmin, 
  rejectEnrollmentCertificateAdmin 
} from '../services/api';
import { toast } from 'react-toastify';
import { 
  CheckCircleIcon, 
  XCircleIcon,
  AcademicCapIcon,
  UserGroupIcon,
  ExclamationTriangleIcon,
  ClockIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';

const AdminEnrollmentManagementPage = () => {
  const [enrollments, setEnrollments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedEnrollment, setSelectedEnrollment] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [certificateRejectionReason, setCertificateRejectionReason] = useState('');

  const fetchEnrollments = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getEnrollmentsForApprovalAdmin();
      if (response && response.data && Array.isArray(response.data.enrollments)) {
        setEnrollments(response.data.enrollments);
      } else {
        console.error('Unexpected response structure or enrollments is not an array:', response);
        setEnrollments([]);
      }
      setError(null);
    } catch (err) {
      setError(err.message || 'Failed to fetch enrollments');
      toast.error(err.response?.data?.message || err.message || 'Failed to fetch enrollments');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchEnrollments();
  }, [fetchEnrollments]);

  const openModal = (enrollment) => {
    setSelectedEnrollment(enrollment);
    setCertificateRejectionReason('');
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedEnrollment(null);
    setCertificateRejectionReason('');
  };

  const handleApproveCertificate = async (enrollmentId) => {
    if (window.confirm('Are you sure you want to approve this certificate?')) {
      try {
        const response = await approveEnrollmentCertificateAdmin(enrollmentId);
        toast.success(response.message || 'Sertifikasi sudah di-approve');
        fetchEnrollments();
      } catch (err) {
        toast.error(err.response?.data?.message || 'Approval gagal, silakan coba lagi.');
      }
    }
  };

  const handleRejectCertificate = async (e) => {
    e.preventDefault();
    if (!selectedEnrollment || !certificateRejectionReason) {
      toast.error('Rejection reason is required.');
      return;
    }
    try {
      await rejectEnrollmentCertificateAdmin(selectedEnrollment.id, {
        rejectionReason: certificateRejectionReason,
      });
      toast.success('Sertifikasi Anda ditolak, silakan ulangi post-test.');
      fetchEnrollments();
      closeModal();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to reject certificate.');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex justify-center items-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Memuat data pendaftaran...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex justify-center items-center">
        <div className="text-center max-w-md mx-auto px-4">
          <div className="bg-red-50 border border-red-200 rounded-xl p-6">
            <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <ExclamationTriangleIcon className="w-6 h-6 text-red-600" />
            </div>
            <h3 className="text-lg font-semibold text-red-800 mb-2">Terjadi Kesalahan</h3>
            <p className="text-red-700">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4">
        {/* Header */}
        <div className="mb-8">
          <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8 border border-gray-100">
            <div className="flex items-center mb-4">
              <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-full flex items-center justify-center mr-4">
                <AcademicCapIcon className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl md:text-3xl font-bold text-gray-800">Manajemen Pendaftaran & Sertifikat</h1>
                <p className="text-gray-600">Kelola persetujuan sertifikat peserta</p>
              </div>
            </div>
          </div>
        </div>

        {/* Content */}
        {enrollments.length === 0 ? (
          <div className="bg-white rounded-2xl shadow-lg p-8 border border-gray-100">
            <div className="text-center">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <UserGroupIcon className="w-8 h-8 text-gray-400" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">Tidak Ada Pendaftaran</h3>
              <p className="text-gray-600">Tidak ada pendaftaran yang memerlukan perhatian.</p>
            </div>
          </div>
        ) : (
          <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 px-6 md:px-8 py-6 border-b border-gray-100">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-full flex items-center justify-center mr-3">
                    <UserGroupIcon className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-gray-900">Daftar Pendaftaran</h2>
                    <p className="text-gray-600 text-sm">{enrollments.length} pendaftaran ditemukan</p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Pengguna</th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Kursus</th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Skor Pre-Test</th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Skor Post-Test</th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tindakan</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {enrollments.map((enrollment) => {
                    const isApproved = enrollment.practicalTestStatus === 'Sertifikat Disetujui';
                    const isRejected = enrollment.practicalTestStatus === 'Sertifikat Ditolak';
                    const isProcessed = isApproved || isRejected;

                    let statusClass = 'text-yellow-600';
                    let statusBgClass = 'bg-yellow-100';
                    let statusText = 'Menunggu Persetujuan';
                    let statusIcon = <ClockIcon className="w-4 h-4" />;
                    
                    if (isApproved) {
                      statusClass = 'text-green-600';
                      statusBgClass = 'bg-green-100';
                      statusText = 'Disetujui';
                      statusIcon = <CheckCircleIcon className="w-4 h-4" />;
                    } else if (isRejected) {
                      statusClass = 'text-red-600';
                      statusBgClass = 'bg-red-100';
                      statusText = 'Ditolak';
                      statusIcon = <XCircleIcon className="w-4 h-4" />;
                    }

                    return (
                      <tr key={enrollment.id} className="hover:bg-gray-50 transition-colors duration-200">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-full flex items-center justify-center text-white font-semibold text-sm mr-3">
                              {enrollment.user?.namaLengkap?.charAt(0)?.toUpperCase() || 'U'}
                            </div>
                            <div>
                              <div className="text-sm font-medium text-gray-900">{enrollment.user?.namaLengkap || 'N/A'}</div>
                              <div className="text-xs text-gray-500">{enrollment.user?.email || 'N/A'}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">{enrollment.course?.judul || 'N/A'}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            {enrollment.preTestScore ?? 'N/A'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            {enrollment.postTestScore ?? 'N/A'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusBgClass} ${statusClass}`}>
                            {statusIcon}
                            <span className="ml-1">{statusText}</span>
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <div className="flex items-center space-x-2">
                            <button
                              onClick={() => handleApproveCertificate(enrollment.id)}
                              className={`p-2 rounded-lg transition-all duration-200 ${
                                isProcessed 
                                  ? 'text-gray-400 cursor-not-allowed' 
                                  : 'text-green-600 hover:text-green-900 hover:bg-green-50'
                              }`}
                              title="Setujui Sertifikat"
                              disabled={isProcessed}
                            >
                              <CheckCircleIcon className="h-5 w-5" />
                            </button>
                            <button
                              onClick={() => openModal(enrollment)}
                              className={`p-2 rounded-lg transition-all duration-200 ${
                                isProcessed 
                                  ? 'text-gray-400 cursor-not-allowed' 
                                  : 'text-red-600 hover:text-red-900 hover:bg-red-50'
                              }`}
                              title="Tolak Sertifikat"
                              disabled={isProcessed}
                            >
                              <XCircleIcon className="h-5 w-5" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Rejection Modal */}
        {isModalOpen && selectedEnrollment && (
          <div className="fixed inset-0 bg-black bg-opacity-50 overflow-y-auto h-full w-full flex justify-center items-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-auto border border-gray-100">
              <div className="flex justify-between items-center p-6 border-b border-gray-100">
                <h3 className="text-lg font-semibold text-gray-900">
                  Tolak Sertifikat untuk {selectedEnrollment.user?.namaLengkap}
                </h3>
                <button
                  onClick={closeModal}
                  className="text-gray-400 hover:text-gray-600 p-2 rounded-lg hover:bg-gray-100 transition-colors duration-200"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>
              <form onSubmit={handleRejectCertificate} className="p-6">
                <div className="mb-4">
                  <label htmlFor="certificateRejectionReason" className="block text-sm font-medium text-gray-700 mb-2">
                    Alasan Penolakan
                  </label>
                  <textarea
                    id="certificateRejectionReason"
                    name="certificateRejectionReason"
                    rows="4"
                    value={certificateRejectionReason}
                    onChange={(e) => setCertificateRejectionReason(e.target.value)}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500 text-sm"
                    placeholder="Masukkan alasan penolakan sertifikat..."
                    required
                  ></textarea>
                </div>
                <div className="flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={closeModal}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors duration-200"
                  >
                    Batal
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 text-sm font-medium text-white bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 rounded-lg transition-all duration-200 shadow-lg hover:shadow-xl"
                  >
                    Tolak Sertifikat
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminEnrollmentManagementPage;
